import React from 'react';
import {useParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {THEME_COLORS, URL_BASE} from "./Globals.ts";
import sharedChatMessageIdMapping from "./Utils/IdMappings/SharedChatMessageIdMapping.ts";
import ChatPreview from "./Components/MiddlePopup/SharePopup/ChatPreview/ChatPreview.tsx";
import Top from "./Components/Top/Top.tsx";
import {useAppStore} from "./GlobalStore/GlobalStore.ts";
import MiddlePopup from "./Components/MiddlePopup/MiddlePopup.tsx";
import {MessageNode} from "./Utils/messageObject/MessageNode.ts";

const fetchAppState = async (uuid: string) => {
    const url = `${URL_BASE}/api/loadSharedChat`;
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ uuid })
    });

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    return await response.json();
};

const SharePage: React.FC = () => {
    const { uuid } = useParams<{ uuid: string }>();
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['sharedChat', uuid],
        queryFn: () => fetchAppState(uuid!),
        enabled: !!uuid,
        retry: false
    });

    let rootNode = null;
    let messagesSequence: any[] = [];

    if (data) {
        const { messagesSequence: fetchedSequence, messages, chatName } = data;

        Object.keys(sharedChatMessageIdMapping).forEach(key => delete sharedChatMessageIdMapping[key]);

        if (fetchedSequence && messages) {
            for (let i = 0; i < messages.length; i++) {
                const { id, childNum, message, children, parent, metadata } = messages[i];
                sharedChatMessageIdMapping[id] = new MessageNode(id, childNum, message, children, parent, metadata);

                if (!parent) rootNode = sharedChatMessageIdMapping[id];
            }
            messagesSequence = fetchedSequence;
            setDynamicStateObj({shareMessagesSequence: messagesSequence, shareChatName: chatName});
        }
    }

    return (
        <div
            className={`${settingsObj.theme} ${THEME_COLORS} flex flex-col h-dvh`}>
            {isLoading ? (
                <div className="flex items-center justify-center h-screen">
                    <p className="text-white">Loading...</p>
                </div>
            ) : isError ? (
                <div className="flex flex-col items-center justify-center h-screen text-2xl font-bold space-y-3">
                    <h1 className={"text-4xl text-red-600"}>404</h1>
                    <h2 className={"text-white"}>Conversation Not Found</h2>
                </div>
            ) : (
                <>
                    <Top isPreview={true}/>
                    <div className={`flex flex-col h-dvh overflow-x-scroll mt-2`}>
                        {rootNode ? (
                            <ChatPreview rootNode={rootNode} messagesSequence={messagesSequence} />
                        ) : (
                            <div className="flex items-center justify-center h-screen">
                                <p className="text-white">No conversation data available.</p>
                            </div>
                        )}
                    </div>
                    <MiddlePopup />
                </>
            )}
        </div>
    );
};

export default SharePage;
